/**
 * Main Configuration Object
 *
 * This file creates the configuration object based on environment variables.
 * All environment variable access should happen here.
 */

import { Config } from './types';
import { parseAllowedDomains, generateCSPConnectSrc } from './pure-utils';
import { environment } from './environment';

// Parse domain lists
const allowedDomains = parseAllowedDomains(
  process.env.NEXT_PUBLIC_ALLOWED_DOMAINS || 'http://localhost:3001,http://localhost:3000'
);

const stripeDomains = parseAllowedDomains(
  process.env.NEXT_PUBLIC_STRIPE_DOMAIN || 'https://js.stripe.com,https://api.stripe.com,https://hooks.stripe.com'
);

const stytchDomains = parseAllowedDomains(
  process.env.NEXT_PUBLIC_STYTCH_DOMAIN || 'https://*.stytch.com,https://stytch.com'
);

const sentryDomains = parseAllowedDomains(
  process.env.NEXT_PUBLIC_SENTRY_DOMAIN || 'https://*.sentry.io'
);

// Backend/Frontend URLs with defaults
const backendHost = process.env.BACKEND_HOST || 'localhost';
const backendPort = process.env.BACKEND_PORT || '3000';
const backendUrl = process.env.BACKEND_URL || `http://${backendHost}:${backendPort}`;
// If NEXT_PUBLIC_API_URL is provided, prefer its origin for allowed list
let derivedApiOrigin: string | undefined;
try {
  if (process.env.NEXT_PUBLIC_API_URL) {
    derivedApiOrigin = new URL(process.env.NEXT_PUBLIC_API_URL).origin;
  }
} catch {}

// Derive WebSocket URL: prefer NEXT_PUBLIC_WS_URL, then derive from NEXT_PUBLIC_API_URL origin, then backendUrl
const derivedWsUrl = (() => {
  if (process.env.NEXT_PUBLIC_WS_URL) {
    return process.env.NEXT_PUBLIC_WS_URL;
  }
  if (derivedApiOrigin) {
    // If API URL is https://api.staging.letsvoley.com/api, use https://api.staging.letsvoley.com for WS
    return derivedApiOrigin;
  }
  return backendUrl;
})();


const frontendHost = process.env.FRONTEND_HOST || 'localhost';
const frontendPort = process.env.FRONTEND_PORT || '3001';
const frontendUrl = process.env.FRONTEND_URL || `http://${frontendHost}:${frontendPort}`;

/**
 * Main configuration object
 * This is the single source of truth for all configuration
 */
export const config: Config = {
  env: environment,

  app: {
    name: process.env.NEXT_PUBLIC_APP_NAME || 'VOLEYIX Messenger',
    version: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
    url: process.env.NEXT_PUBLIC_APP_URL || frontendUrl,
    inactivityMinutes: parseInt(process.env.NEXT_PUBLIC_INACTIVITY_MINUTES || '60', 10),
  },

  api: {
    baseUrl: process.env.NEXT_PUBLIC_API_URL || `${backendUrl}/api`,
    version: process.env.NEXT_PUBLIC_API_VERSION || 'v1',
    timeout: parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT || '30000', 10),
  },

  websocket: {
    url: derivedWsUrl,
    // IMPORTANT: Multi-Replica Deployment Fix
    // Set NEXT_PUBLIC_FORCE_WEBSOCKET_ONLY=true in staging/production to avoid
    // "Session ID unknown" errors caused by polling requests hitting different containers.
    // WebSocket connections are persistent and stay on one container once established.
    // Also set FORCE_WEBSOCKET_ONLY=true on the backend.
    forceWebSocketOnly: process.env.NEXT_PUBLIC_FORCE_WEBSOCKET_ONLY === 'true',
    config: {
      // CRITICAL: Explicit path prevents proxy/load balancer path rewriting issues
      // Must match the server's default Socket.IO path (do not duplicate or change)
      path: '/socket.io',
      // Transport configuration:
      // - WebSocket-only (staging/prod): Avoids "Session ID unknown" errors from multi-replica routing
      // - Polling first (local dev): More reliable behind proxies/firewalls during development
      transports: process.env.NEXT_PUBLIC_FORCE_WEBSOCKET_ONLY === 'true'
        ? ['websocket']
        : ['polling', 'websocket'],
      // CRITICAL: Disable upgrade when using WebSocket-only to prevent any polling requests
      // When upgrade=true, Socket.IO may still try polling first before upgrading
      upgrade: process.env.NEXT_PUBLIC_FORCE_WEBSOCKET_ONLY !== 'true',
      rememberUpgrade: process.env.NEXT_PUBLIC_FORCE_WEBSOCKET_ONLY !== 'true',
      timeout: 20000,
      // Disable Socket.IO built-in reconnection - SocketClient handles reconnection with better throttling
      reconnection: false,
      reconnectionAttempts: 0,
      withCredentials: true,
      forceNew: false, // CRITICAL: Reuse existing connection
      multiplex: true, // CRITICAL: Share one connection across namespaces
    },
  },

  auth: {
    stytch: {
      publicToken: process.env.NEXT_PUBLIC_STYTCH_PUBLIC_TOKEN || '',
      domain: stytchDomains,
    },
    cookieDomain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
  },

  payments: {
    stripe: {
      publishableKey: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || '',
      domains: stripeDomains,
    },
  },

  monitoring: {
    sentry: {
      dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
      enabled: environment === 'production' || environment === 'staging' || process.env.NEXT_PUBLIC_ENABLE_ERROR_REPORTING === 'true',
      domains: sentryDomains,
      release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
    },
  },

  features: {
    smsEnabled: process.env.NEXT_PUBLIC_SMS_ENABLED !== 'false', // Default true
    debugLogging: environment === 'development',
    errorReporting: environment === 'production' || environment === 'staging' || process.env.NEXT_PUBLIC_ENABLE_ERROR_REPORTING === 'true',
    reduxTrace: process.env.REDUX_TRACE === 'true',
    runRequestsWithoutTimeout: process.env.NEXT_PUBLIC_RUN_REQUESTS_WITHOUT_TIMEOUT === 'true',
  },

  security: {
    allowedDomains: Array.from(new Set([...allowedDomains, ...(derivedApiOrigin ? [derivedApiOrigin] : [])])),
    allowedImageDomains: parseAllowedDomains(
      process.env.NEXT_PUBLIC_ALLOWED_IMAGE_DOMAINS || allowedDomains.join(',')
    ),
    csp: {
      connectSrc: generateCSPConnectSrc(allowedDomains, stripeDomains, stytchDomains, sentryDomains),
    },
  },

  external: {
    avatarApiUrl: process.env.NEXT_PUBLIC_AVATAR_API_URL || 'https://ui-avatars.com/api/',
  },

  backend: {
    host: backendHost,
    port: backendPort,
    url: backendUrl,
  },

  frontend: {
    host: frontendHost,
    port: frontendPort,
    url: frontendUrl,
  },
};